import { type ChangeEvent, memo, useCallback, useId, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Checkbox } from '@m/alchemy-ui/Checkbox';
import { Select } from '@m/alchemy-ui/Select';
import { type PanelConfigPatch } from '../../common/panel-grid';
import { type PageCmsPanel } from '../../common/panel-types';
import { PanelEditorLabel, PanelEditorSection } from '../../CustomPage.styled';
import { panelEditorMessages } from './panel-editor-messages';

const messages = defineMessages({
  allChallenges: {
    id: 'editCustomPageAllChallenges',
    defaultMessage: 'All challenges'
  },
  featuredOnly: {
    id: 'featuredOnly',
    defaultMessage: 'Featured only'
  },
  hideDescription: {
    id: 'hideDescription',
    defaultMessage: 'Hide description'
  },
  includeUserChallenges: {
    id: 'includeUserChallenges',
    defaultMessage: 'Include user created challenges'
  }
});

const ALL_CHALLENGES = 'all';
const FEATURED_ONLY = 'featured';

type ChallengeCarouselConfig = Extract<PageCmsPanel['settings'], { __typename: 'ChallengeCarouselPanelSettings' }>;

interface ChallengeCarouselPanelEditorProps {
  readonly config: ChallengeCarouselConfig;
  readonly onConfigChange: (patch: PanelConfigPatch) => void;
}

const ChallengeCarouselPanelEditorComponent = ({ config, onConfigChange }: ChallengeCarouselPanelEditorProps) => {
  const intl = useIntl();
  const id = useId();
  const displayLabelId = `challenge-carousel-display-label-${id}`;

  const displayOptions = useMemo(
    () => [
      { value: ALL_CHALLENGES, label: intl.formatMessage(messages.allChallenges) },
      { value: FEATURED_ONLY, label: intl.formatMessage(messages.featuredOnly) }
    ],
    [intl]
  );

  const handleFavouritesChange = useCallback(
    (option: { value: string }) => onConfigChange({ favourites: option.value === FEATURED_ONLY }),
    [onConfigChange]
  );

  const handleHideDescriptionChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onConfigChange({ hideDescription: event.target.checked }),
    [onConfigChange]
  );

  const handleIncludeUserChallengesChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => onConfigChange({ includeUserChallenges: event.target.checked }),
    [onConfigChange]
  );

  return (
    <>
      <PanelEditorSection>
        <PanelEditorLabel id={displayLabelId}>{intl.formatMessage(panelEditorMessages.display)}</PanelEditorLabel>
        <Select
          labelId={displayLabelId}
          isFullWidth
          value={config.favourites ? FEATURED_ONLY : ALL_CHALLENGES}
          options={displayOptions}
          onChange={handleFavouritesChange}
          data-testid="challenge-carousel-panel-display-select"
        />
      </PanelEditorSection>
      <PanelEditorSection>
        <Checkbox
          label={intl.formatMessage(messages.hideDescription)}
          checked={config.hideDescription}
          onChange={handleHideDescriptionChange}
          data-testid="challenge-carousel-panel-hide-description-checkbox"
        />
        <Checkbox
          label={intl.formatMessage(messages.includeUserChallenges)}
          checked={config.includeUserChallenges}
          onChange={handleIncludeUserChallengesChange}
          data-testid="challenge-carousel-panel-include-user-challenges-checkbox"
        />
      </PanelEditorSection>
    </>
  );
};

// Immutable config updates make prop comparison cheap: the editor re-renders only when its
// own config (or the grid callback) changes.
export const ChallengeCarouselPanelEditor = memo(ChallengeCarouselPanelEditorComponent);
